/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment Form
 *
 * @file AssignmentForm.jsx
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { Button, IconButton } from '@mui/material';
import Icon from '../icons/Icon';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import AssignmentDateField from './AssignmentDateField';
import AssignmentTimeField from './AssignmentTimeField';
import AssignmentTimezoneField from './AssignmentTimezoneField';
import ReadonlyAssignmentNameField from './ReadonlyAssignmentNameField';
import AssignmentNameField from './AssignmentNameField';
import { AssignmentContentPicker, ChapterContentProvider } from '../chapter-content';
import { computeNextSelection } from '../chapter-content/chapterContentTreeUtils';
import {
  ASSIGNMENT_FORM_MODE,
  isActionDisabled
} from './assignmentFormActions';
import {
  buildContentPickerSummary,
  buildFormDefaultValues,
  buildRestrictedTocData
} from './assignmentFormUtils';
import {
  createAssignmentNameValidator,
  createChaptersValidator,
  createDueDateValidator,
  createDueTimeValidator
} from './assignmentFormValidation';
import { AssignmentsStylesRoot, classes } from './styles/assignmentsStyles';

export const defaultConfig = {
  showAddContentButton: true,
  contentTreeVariant: 'picker',
  assignmentNameReadonly: false,
  showAssignmentNameField: true,
  generateTitleOnContentConfirm: false,
  showTimeZoneField: false,
  showBackNav: false
};

const ACTION_VARIANT_CLASS = {
  finish: 'finishButton',
  saveAndAdd: 'saveAndAddButton',
  cancel: 'cancelButton',
  save: 'saveButton'
};

function areChaptersEqual(currentChapters = [], baselineChapters = []) {
  const toIds = (chapters) => new Set(
    chapters.map((chapter) => (typeof chapter === 'object' ? chapter.id : chapter))
  );
  const currentIds = toIds(currentChapters);
  const baselineIds = toIds(baselineChapters);

  if (currentIds.size !== baselineIds.size) {
    return false;
  }

  return Array.from(currentIds).every((id) => baselineIds.has(id));
}

function isAssignmentNameEditable(hasContent, isTitleGenerating, generateTitleOnContentConfirm) {
  if (!generateTitleOnContentConfirm) {
    return true;
  }

  return hasContent && !isTitleGenerating;
}

function getDisabledNamePlaceholder(textInfo, hasContent) {
  if (!hasContent) {
    return textInfo.assignmentNameDisabledPlaceholder || '';
  }

  return textInfo.assignmentNameGeneratedPlaceholder || '';
}

function getDisabledNameDisplayValue({ isTitleGenerating, assignmentName, placeholder }) {
  if (isTitleGenerating) {
    return placeholder;
  }

  return assignmentName || placeholder;
}

function getContentLinkLabel(textInfo, hasContent) {
  if (hasContent) {
    return textInfo.editContentLinkLabel ?? textInfo.contentLinkLabel;
  }

  return textInfo.contentLinkLabel;
}

function getDisplaySections(mode, hasContent, contentSectionNumbers, editSections) {
  if (mode === ASSIGNMENT_FORM_MODE.CREATE && hasContent) {
    return contentSectionNumbers;
  }

  return editSections;
}

function getActionHasContentRequirement(generateTitleOnContentConfirm, hasContent) {
  if (!generateTitleOnContentConfirm) {
    return true;
  }

  return hasContent;
}

const AssignmentForm = ({
  mode,
  initialValues,
  textInfo,
  actions,
  config: configProp = {},
  classes: classesProp = {},
  formKey,
  tocData,
  chapterMcqMapping,
  restrictAssigned,
  existingAssignments,
  currentAssignmentIndex,
  onScheduleMenuAction,
  onAddToDate,
  onContentConfirmed,
  sections,
  sectionsPrefix,
  timeZoneOptions,
  onTimeZoneChange,
  onBackNavClick
}) => {
  const config = { ...defaultConfig, ...configProp };
  // Append the host-supplied override class (if any) alongside our own scoped
  // class for that slot, matching the old withStyles `classes` prop merge.
  const mergeClassName = (key) => [classes[key], classesProp[key]].filter(Boolean).join(' ');
  const {
    generateTitleOnContentConfirm,
    showAssignmentNameField,
    showTimeZoneField,
    existingAssignmentTitles,
    titleValidationConfig,
    disableTreeViewCheckbox,
    showBackNav
  } = config;
  const isContentEditDisabled = Boolean(disableTreeViewCheckbox);
  const showTimeZone = showTimeZoneField && timeZoneOptions?.length > 0;
  const formDefaults = buildFormDefaultValues(initialValues, timeZoneOptions);
  const [isContentViewActive, setIsContentViewActive] = useState(false);
  const [isTitleGenerating, setIsTitleGenerating] = useState(false);
  const contentPickerBaselineRef = useRef(null);

  const effectiveTocData = useMemo(
    () => buildRestrictedTocData(tocData, existingAssignments, {
      restrictAssigned,
      excludeAssignmentIndex: currentAssignmentIndex
    }),
    [tocData, existingAssignments, restrictAssigned, currentAssignmentIndex]
  );

  const {
    control,
    watch,
    reset,
    getValues,
    setValue,
    trigger,
    formState: { errors, isValid, isDirty }
  } = useForm({
    defaultValues: formDefaults,
    mode: 'onChange'
  });

  const values = watch();
  const selectedChapters = useMemo(() => values.chapters || [], [values.chapters]);
  const initialChapters = formDefaults.chapters || [];
  const hasContent = selectedChapters.length > 0;
  const isNameEditable = isAssignmentNameEditable(
    hasContent,
    isTitleGenerating,
    generateTitleOnContentConfirm
  );
  // While the name field is readonly (waiting for content or Berlin title gen), the visible UI
  // is not a react-hook-form input. Register assignmentName invisibly so validation and isValid
  // still work; AssignmentNameField takes over once the name becomes editable.
  const needsHiddenAssignmentNameController = showAssignmentNameField
    && generateTitleOnContentConfirm
    && !isNameEditable;

  const dueDateRules = useMemo(
    () => ({
      validate: createDueDateValidator(getValues),
      deps: ['dueTime'] // tells react-hook-form to re-validate the due date when the due time changess
    }),
    [getValues]
  );

  const dueTimeRules = useMemo(
    () => ({
      validate: createDueTimeValidator()
    }),
    []
  );

  const chaptersRules = useMemo(
    () => ({
      validate: createChaptersValidator({
        tocData: effectiveTocData,
        chapterMcqMapping,
        getValues
      })
    }),
    [effectiveTocData, chapterMcqMapping, getValues]
  );

  const contentSummary = useMemo(
    () => buildContentPickerSummary(selectedChapters, effectiveTocData, chapterMcqMapping),
    [selectedChapters, effectiveTocData, chapterMcqMapping]
  );

  const displaySections = getDisplaySections(
    mode,
    hasContent,
    contentSummary.sectionNumbers,
    sections
  );

  const assignmentNameRules = useMemo(
    () => ({
      validate: createAssignmentNameValidator({
        hasContent,
        isTitleGenerating,
        mode,
        showAssignmentNameField,
        existingAssignmentTitles,
        titleValidationConfig
      })
    }),
    [
      hasContent,
      isTitleGenerating,
      mode,
      showAssignmentNameField,
      existingAssignmentTitles,
      titleValidationConfig
    ]
  );

  // Reset only when formKey changes (new add session, save-and-add, edit target).
  // initialValues is omitted from deps — parent rebuilds a new object each render;
  // syncing on reference would wipe in-progress edits (e.g. browser resize).
  useEffect(() => {
    reset(buildFormDefaultValues(initialValues, timeZoneOptions));
    setIsContentViewActive(false);
    setIsTitleGenerating(false);
    const fieldsToTrigger = ['dueDate', 'dueTime', 'chapters', 'assignmentName'];

    if (showTimeZoneField && timeZoneOptions?.length > 0) {
      fieldsToTrigger.push('timeZone');
    }

    trigger(fieldsToTrigger);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on formKey only; see comment above
  }, [formKey, reset, trigger]);

  useEffect(() => {
    if (!generateTitleOnContentConfirm) {
      return;
    }

    trigger('assignmentName');
  }, [generateTitleOnContentConfirm, hasContent, isTitleGenerating, trigger]);

  const actionsDisabled = isActionDisabled({
    mode,
    isValid,
    isDirty,
    hasContent: getActionHasContentRequirement(generateTitleOnContentConfirm, hasContent),
    isTitleGenerating
  });

  const isActionFormDisabled = (action) => {
    if (action.variant === 'cancel' || action.disableOnInvalid === false) {
      return false;
    }

    return actionsDisabled;
  };

  const handleOpenContentView = () => {
    contentPickerBaselineRef.current = getValues('chapters') || [];
    setIsContentViewActive(true);
  };

  const handleCloseContentView = () => {
    const baseline = contentPickerBaselineRef.current ?? initialChapters;

    setValue('chapters', baseline, {
      shouldValidate: true,
      shouldDirty: !areChaptersEqual(baseline, initialChapters)
    });
    setIsContentViewActive(false);
  };

  const handleNodeToggle = (node) => {
    const nextChapters = computeNextSelection(node, getValues('chapters') || []);

    setValue('chapters', nextChapters, {
      shouldDirty: !areChaptersEqual(nextChapters, initialChapters),
      shouldValidate: true
    });
  };

  const handleContentSave = async () => {
    const baseline = contentPickerBaselineRef.current ?? [];
    const chapters = getValues('chapters') || [];

    if (areChaptersEqual(chapters, baseline)) {
      setIsContentViewActive(false);

      return;
    }

    const valid = await trigger('chapters');

    if (!valid) {
      return;
    }

    setIsContentViewActive(false);
    contentPickerBaselineRef.current = chapters;

    if (!generateTitleOnContentConfirm) {
      return;
    }

    setIsTitleGenerating(true);

    try {
      const title = await onContentConfirmed(getValues());

      if (title) {
        setValue('assignmentName', title, { shouldDirty: true, shouldValidate: true });
      }
    } finally {
      setIsTitleGenerating(false);
    }
  };

  const handleDueTimeChange = async () => {
    await trigger('dueDate');
    await trigger('dueTime');
  };

  const pickerBaseline = contentPickerBaselineRef.current;
  const hasPickerChapterChanges = pickerBaseline
    ? !areChaptersEqual(selectedChapters, pickerBaseline)
    : false;
  const contentHasSelection = selectedChapters.length > 0;
  const isContentSaveDisabled = !contentHasSelection
    || Boolean(errors.chapters)
    || !hasPickerChapterChanges;

  const contentActions = [
    {
      id: 'content-finish',
      type: 'button',
      text: textInfo.cancelLabel,
      variant: 'finish',
      disabled: false,
      callback: handleCloseContentView
    },
    {
      id: 'content-save',
      type: 'button',
      text: textInfo.contentLinkLabel,
      variant: 'save',
      disabled: isContentSaveDisabled,
      callback: handleContentSave
    }
  ];

  const renderAction = (action) => {
    if (action.type === 'custom' && action.element) {
      return action.element;
    }

    if (!action.callback) {
      return null;
    }

    const disabled = action.disabled || isActionFormDisabled(action);
    const buttonClass = classes[ACTION_VARIANT_CLASS[action.variant]] || classes.finishButton;

    const handleClick = () => {
      if (disabled) {
        return;
      }

      action.callback(getValues());
    };

    if (action.iconName && action.text) {
      return (
        <Button
          type="button"
          className={buttonClass}
          onClick={handleClick}
          disabled={disabled}
          startIcon={<Icon name={action.iconName} />}
        >
          <span>{action.text}</span>
        </Button>
      );
    }

    return (
      <Button
        type="button"
        className={buttonClass}
        onClick={handleClick}
        disabled={disabled}
      >
        <span>{action.text}</span>
      </Button>
    );
  };

  const renderActions = () => {
    if (!actions?.length) {
      return null;
    }

    return (
      <div className={mergeClassName('actionsRow')}>
        {actions.map((action, index) => (
          <React.Fragment key={action.id || action.text || index}>
            {renderAction(action)}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const contentLinkLabel = getContentLinkLabel(textInfo, hasContent);

  /** Back nav beside the form title, for flows that own the page (e.g. welcome manual setup). */
  const renderBackNav = () => {
    if (!showBackNav) {
      return null;
    }

    return (
      <IconButton
        aria-label={textInfo.backLabel || ''}
        className={classes.backNavButton}
        onClick={onBackNavClick}
      >
        <Icon name="BackIcon" className={classes.backNavIcon} />
      </IconButton>
    );
  };

  const renderHeader = () => (
    <div className={classes.sectionHeader}>
      {renderBackNav()}
      <h2 className={classes.sectionTitle}>{textInfo.headerTitle}</h2>
    </div>
  );

  const renderAddContentButton = () => {
    if (!config.showAddContentButton) {
      return null;
    }

    return (
      <div className={classes.addContentRow}>
        <TooltipWrapper
          tooltip={isContentEditDisabled ? (textInfo.editContentDisabledTooltip || '') : ''}
          placement="top"
          showTooltip={isContentEditDisabled}
        >
          <Button
            type="button"
            className={mergeClassName('addContentButton')}
            onClick={handleOpenContentView}
            disabled={isContentEditDisabled}
          >
            <Icon name="AddContentIcon" />
            {contentLinkLabel}
          </Button>
        </TooltipWrapper>
      </div>
    );
  };

  // PPCOREAH-1598: allow multiple assignments on the same due date (no occupied-date filterDate).
  const renderDueDateRow = () => (
    <div className={classes.dueDateRow}>
      <div className={classes.dateField}>
        <AssignmentDateField
          name="dueDate"
          control={control}
          label={textInfo.dueDateLabel}
          rules={dueDateRules}
          dateFormat={textInfo.dateFormat}
          placeholderText={textInfo.datePlaceholder}
        />
      </div>
      <div className={classes.timeField}>
        <AssignmentTimeField
          name="dueTime"
          control={control}
          label={textInfo.dueTimeLabel}
          rules={dueTimeRules}
          onAfterChange={handleDueTimeChange}
        />
      </div>
    </div>
  );

  const renderAssignmentNameField = () => {
    if (!showAssignmentNameField) {
      return null;
    }

    if (isNameEditable) {
      return (
        <AssignmentNameField
          name="assignmentName"
          control={control}
          label={textInfo.assignmentNameLabel}
          classes={classes}
          rules={assignmentNameRules}
        />
      );
    }

    const placeholder = getDisabledNamePlaceholder(textInfo, hasContent);
    const displayValue = getDisabledNameDisplayValue({
      isTitleGenerating,
      assignmentName: values.assignmentName,
      placeholder
    });

    return (
      <ReadonlyAssignmentNameField
        classes={classes}
        label={textInfo.assignmentNameLabel}
        value={displayValue}
        id="assignment-form-assignment-name"
        disabled
        isLoading={isTitleGenerating}
      />
    );
  };

  const renderContentPicker = () => {
    const handleScheduleMenuAction = (menuItemId, node) => {
      onScheduleMenuAction?.(menuItemId, node, getValues());
    };

    return (
      <ChapterContentProvider
        value={{
          nodes: effectiveTocData,
          questionCounts: chapterMcqMapping,
          textInfo,
          restrictAssigned,
          variant: config.contentTreeVariant,
          onScheduleMenuAction: handleScheduleMenuAction,
          onAddToDate
        }}
      >
        <AssignmentContentPicker
          selectedChapters={selectedChapters}
          onNodeToggle={handleNodeToggle}
          summary={contentSummary}
          actions={contentActions}
          onBack={handleCloseContentView}
        />
      </ChapterContentProvider>
    );
  };

  const renderSections = () => {
    if (!displaySections?.length) {
      return null;
    }

    return (
      <div className={mergeClassName('editSectionsRow')}>
        <span className={classes.editSectionsPrefix}>{sectionsPrefix}</span>
        {displaySections.map((section) => (
          <span key={section} className={classes.editSectionChip}>
            {section}
          </span>
        ))}
      </div>
    );
  };

  const renderContentSection = () => {
    const sectionsContent = renderSections();
    const addContentButton = renderAddContentButton();

    if (!sectionsContent && !addContentButton) {
      return null;
    }

    return (
      <div className={classes.contentSection}>
        {sectionsContent}
        {addContentButton}
      </div>
    );
  };

  const renderTimeZoneField = () => {
    if (!showTimeZone) {
      return null;
    }

    return (
      <div className={classes.timeZoneField}>
        <AssignmentTimezoneField
          name="timeZone"
          control={control}
          label={textInfo.timeZoneLabel}
          options={timeZoneOptions}
          rules={{
            required: textInfo.timeZoneRequiredError
          }}
          onChange={onTimeZoneChange}
        />
      </div>
    );
  };

  const renderMainForm = () => (
    <AssignmentsStylesRoot className={classes.formPane}>
      {renderHeader()}
      {renderContentSection()}

      <div className={classes.formBody}>
        {renderDueDateRow()}
        {renderTimeZoneField()}
        {renderAssignmentNameField()}
      </div>

      {renderActions()}
    </AssignmentsStylesRoot>
  );

  return (
    <>
      <Controller
        name="chapters"
        control={control}
        rules={chaptersRules}
        render={() => null}
      />
      {needsHiddenAssignmentNameController && (
        <Controller
          name="assignmentName"
          control={control}
          rules={assignmentNameRules}
          render={() => null}
        />
      )}
      {isContentViewActive ? renderContentPicker() : renderMainForm()}
    </>
  );
};

AssignmentForm.propTypes = {
  mode: PropTypes.oneOf([ASSIGNMENT_FORM_MODE.CREATE, ASSIGNMENT_FORM_MODE.EDIT]).isRequired,
  initialValues: PropTypes.object,
  textInfo: PropTypes.object.isRequired,
  actions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.oneOf(['button', 'custom']),
    text: PropTypes.string,
    iconName: PropTypes.string,
    variant: PropTypes.oneOf(['finish', 'saveAndAdd', 'cancel', 'save']),
    callback: PropTypes.func,
    disabled: PropTypes.bool,
    disableOnInvalid: PropTypes.bool,
    element: PropTypes.node
  })),
  config: PropTypes.object,
  classes: PropTypes.object,
  formKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tocData: PropTypes.array,
  chapterMcqMapping: PropTypes.array,
  restrictAssigned: PropTypes.bool,
  existingAssignments: PropTypes.array,
  currentAssignmentIndex: PropTypes.number,
  onScheduleMenuAction: PropTypes.func,
  onAddToDate: PropTypes.func,
  onContentConfirmed: PropTypes.func,
  sections: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  sectionsPrefix: PropTypes.string,
  timeZoneOptions: PropTypes.array,
  onTimeZoneChange: PropTypes.func,
  onBackNavClick: PropTypes.func
};

AssignmentForm.defaultProps = {
  initialValues: {},
  actions: [],
  config: defaultConfig,
  classes: {},
  formKey: 0,
  tocData: [],
  chapterMcqMapping: [],
  restrictAssigned: true,
  existingAssignments: [],
  currentAssignmentIndex: null,
  onScheduleMenuAction: null,
  onAddToDate: null,
  onContentConfirmed: null,
  sections: [],
  sectionsPrefix: '',
  timeZoneOptions: [],
  onTimeZoneChange: null,
  onBackNavClick: null
};

export default AssignmentForm;
